import { computed, ref } from "vue";

export interface RowClickModifiers {
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}

export function useFieldSelection() {
  const selectedIds = ref<Set<string>>(new Set());
  const anchorId = ref<string | null>(null);

  const selectionCount = computed<number>(() => selectedIds.value.size);
  const hasSelection = computed<boolean>(() => selectedIds.value.size > 0);

  function isSelected(id: string): boolean {
    return selectedIds.value.has(id);
  }

  function setSelection(ids: readonly string[]): void {
    selectedIds.value = new Set(ids);
  }

  function toggle(id: string): void {
    if (!id) {
      return;
    }
    const next = new Set(selectedIds.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    selectedIds.value = next;
    anchorId.value = id;
  }

  function selectRange(toId: string, visibleOrder: readonly string[]): void {
    if (!anchorId.value) {
      setSelection([toId]);
      anchorId.value = toId;
      return;
    }
    const a = visibleOrder.indexOf(anchorId.value);
    const b = visibleOrder.indexOf(toId);
    if (a === -1 || b === -1) {
      setSelection([toId]);
      anchorId.value = toId;
      return;
    }
    const [lo, hi] = a < b ? [a, b] : [b, a];
    setSelection(visibleOrder.slice(lo, hi + 1));
  }

  function handleRowClick(
    id: string,
    modifiers: RowClickModifiers,
    visibleOrder: readonly string[]
  ): void {
    if (!id) {
      return;
    }
    if (modifiers.shiftKey && anchorId.value) {
      selectRange(id, visibleOrder);
      return;
    }
    if (modifiers.metaKey || modifiers.ctrlKey) {
      toggle(id);
      return;
    }
    setSelection([id]);
    anchorId.value = id;
  }

  function remove(ids: Iterable<string>): void {
    const next = new Set(selectedIds.value);
    for (const id of ids) {
      next.delete(id);
    }
    selectedIds.value = next;
  }

  function clear(): void {
    selectedIds.value = new Set();
    anchorId.value = null;
  }

  return {
    selectedIds,
    selectionCount,
    hasSelection,
    anchorId,
    isSelected,
    setSelection,
    toggle,
    selectRange,
    handleRowClick,
    remove,
    clear,
  };
}
